import { t } from "../i18n/index.js";
import {
  DEFAULT_TOUCH_PREFS,
  layoutTouch,
  readSafeInsets,
  saveTouchPrefs,
  spotFromPoint,
  touchPrefs,
  type TouchPrefs,
} from "../input/touchLayout.js";
import { haptic, hapticsAvailable } from "../mobile/haptics.js";
import { DASH_GLYPH, KICK_GLYPH } from "../input/touchControls.js";
import "../mobile/touchControls.css";
import "./touchEditor.css";

/**
 * TU MANDO: el mando tactil a la medida de tu pulgar.
 *
 * Es CONTROLES en tactil (el mismo boton pequeño del menu: aqui no hay teclas
 * que cambiar, pero si un pulgar que no es el de nadie mas). Se ven los
 * botones donde estaran en el partido y se arrastran a donde caiga el dedo;
 * se agrandan, se pasan al otro lado para los zurdos, y se elige si el movil
 * vibra y si tocar el campo golpea. Se guarda al momento.
 */
export function openTouchEditor(root: HTMLElement = document.body): Promise<void> {
  let prefs: TouchPrefs = touchPrefs();

  const overlay = document.createElement("div");
  overlay.className = "touch-editor";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "touch-editor-title");
  overlay.innerHTML = `
    <div class="touch-editor__zone" data-zone>
      <span>${t("Joystick: donde apoyes el pulgar")}</span>
    </div>
    <button type="button" class="golazo-touch__btn golazo-touch__btn--kick touch-editor__btn" data-drag="kick"
      aria-label="${t("Golpear")}">${KICK_GLYPH}</button>
    <button type="button" class="golazo-touch__btn golazo-touch__btn--dash touch-editor__btn" data-drag="dash"
      aria-label="${t("Correr")}">${DASH_GLYPH}</button>
    <div class="touch-editor__panel">
      <h2 id="touch-editor-title">${t("TU MANDO")}</h2>
      <p class="touch-editor__lead">${t("Arrastra los botones a donde caiga tu pulgar.")}</p>
      <label class="touch-editor__row">
        <span>${t("Tamaño")}</span>
        <input type="range" min="0.8" max="1.35" step="0.05" data-size />
      </label>
      <label class="touch-editor__row touch-editor__check">
        <input type="checkbox" data-field />
        <span>${t("Tocar el campo golpea hacia allí")}</span>
      </label>
      <label class="touch-editor__row touch-editor__check" data-vibration-row>
        <input type="checkbox" data-vibration />
        <span>${t("Vibración")}</span>
      </label>
      <label class="touch-editor__row touch-editor__check">
        <input type="checkbox" data-left />
        <span>${t("Zurdo (joystick a la derecha)")}</span>
      </label>
      <div class="touch-editor__actions">
        <button type="button" class="touch-editor__reset" data-reset>${t("Restablecer")}</button>
        <button type="button" class="touch-editor__ok" data-close>${t("Listo")}</button>
      </div>
    </div>`;
  root.appendChild(overlay);

  const zone = overlay.querySelector<HTMLElement>("[data-zone]")!;
  const buttons = {
    kick: overlay.querySelector<HTMLElement>('[data-drag="kick"]')!,
    dash: overlay.querySelector<HTMLElement>('[data-drag="dash"]')!,
  };
  const size = overlay.querySelector<HTMLInputElement>("[data-size]")!;
  const field = overlay.querySelector<HTMLInputElement>("[data-field]")!;
  const vibration = overlay.querySelector<HTMLInputElement>("[data-vibration]")!;
  const left = overlay.querySelector<HTMLInputElement>("[data-left]")!;
  if (!hapticsAvailable()) overlay.querySelector<HTMLElement>("[data-vibration-row]")!.hidden = true;

  const render = (): void => {
    const layout = layoutTouch(window.innerWidth, window.innerHeight, prefs, readSafeInsets());
    for (const [name, box] of [
      ["kick", layout.kick],
      ["dash", layout.dash],
    ] as const) {
      const el = buttons[name];
      el.style.setProperty("--size", `${box.size}px`);
      el.style.left = `${box.cx - box.size / 2}px`;
      el.style.top = `${box.cy - box.size / 2}px`;
    }
    zone.style.left = `${layout.stickFrom}px`;
    zone.style.width = `${layout.stickTo - layout.stickFrom}px`;
    size.value = String(prefs.size);
    field.checked = prefs.fieldTouch;
    vibration.checked = prefs.vibration;
    left.checked = prefs.leftHanded;
  };

  const commit = (next: TouchPrefs): void => {
    prefs = next;
    saveTouchPrefs(prefs);
    render();
  };

  render();

  // Arrastrar un boton: el centro sigue al dedo; al soltar, se guarda.
  let drag: { name: "kick" | "dash"; pointerId: number; dx: number; dy: number } | null = null;
  const onDown = (event: PointerEvent): void => {
    const el = (event.target as HTMLElement).closest<HTMLElement>("[data-drag]");
    if (!el) return;
    const name = el.dataset.drag as "kick" | "dash";
    const rect = el.getBoundingClientRect();
    drag = {
      name,
      pointerId: event.pointerId,
      dx: event.clientX - (rect.left + rect.width / 2),
      dy: event.clientY - (rect.top + rect.height / 2),
    };
    try {
      el.setPointerCapture(event.pointerId);
    } catch {
      /* puntero ya suelto */
    }
    el.classList.add("is-dragging");
    haptic("press");
    event.preventDefault();
  };
  const onMove = (event: PointerEvent): void => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const spot = spotFromPoint(
      event.clientX - drag.dx,
      event.clientY - drag.dy,
      window.innerWidth,
      window.innerHeight,
      prefs.leftHanded,
      readSafeInsets(),
    );
    prefs = { ...prefs, [drag.name]: spot };
    render();
    event.preventDefault();
  };
  const onUp = (event: PointerEvent): void => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    buttons[drag.name].classList.remove("is-dragging");
    drag = null;
    commit(prefs);
  };
  overlay.addEventListener("pointerdown", onDown);
  overlay.addEventListener("pointermove", onMove);
  overlay.addEventListener("pointerup", onUp);
  overlay.addEventListener("pointercancel", onUp);

  size.addEventListener("input", () => commit({ ...prefs, size: Number(size.value) }));
  field.addEventListener("change", () => commit({ ...prefs, fieldTouch: field.checked }));
  vibration.addEventListener("change", () => {
    commit({ ...prefs, vibration: vibration.checked });
    // Que se sienta lo que se acaba de encender.
    if (vibration.checked) haptic("perfect");
  });
  left.addEventListener("change", () => commit({ ...prefs, leftHanded: left.checked }));
  window.addEventListener("resize", render);

  return new Promise((resolve) => {
    const close = (): void => {
      window.removeEventListener("resize", render);
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    window.addEventListener("keydown", onKey, true);
    overlay.querySelector("[data-close]")!.addEventListener("click", close);
    overlay.querySelector("[data-reset]")!.addEventListener("click", () => commit({ ...DEFAULT_TOUCH_PREFS }));
  });
}
